// PDF / sheet helpers shared by the issue pages and doc control.
//
//   resolveDocumentUrl(link)   -> Promise<string>  usable by <img> / pdf.js:
//                                 gz links are fetched + inflated into a blob
//                                 URL, server-relative paths get the API origin.
//   renderPdfThumbnail(link)   -> Promise<dataURL|null>  first page at a small
//                                 scale, cached per link (module-wide).
//   loadPdfJs()                -> Promise<pdfjsLib>  same CDN build the doc
//                                 control page and IssueTool already use.
import pako from "pako";
import { url as apiUrl } from "api";

const PDFJS_SRC = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
const PDFJS_WORKER = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

let pdfjsPromise = null;
export function loadPdfJs() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.pdfjsLib) {
    if (!window.pdfjsLib.GlobalWorkerOptions.workerSrc) window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    return Promise.resolve(window.pdfjsLib);
  }
  if (!pdfjsPromise) {
    pdfjsPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(`script[src="${PDFJS_SRC}"]`);
      const script = existing || document.createElement("script");
      const done = () => {
        if (!window.pdfjsLib) { reject(new Error("pdf.js failed to load")); return; }
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
        resolve(window.pdfjsLib);
      };
      script.addEventListener("load", done);
      script.addEventListener("error", () => reject(new Error("pdf.js failed to load")));
      if (!existing) { script.src = PDFJS_SRC; document.head.appendChild(script); }
      else if (window.pdfjsLib) done();
    }).catch((e) => { pdfjsPromise = null; throw e; });
  }
  return pdfjsPromise;
}

const isAbsolute = (u) => /^(https?:|blob:|data:)/i.test(u);
const isGz = (u) => /\.gz(\?.*)?$/i.test(String(u || "").split("#")[0]);

/** Absolute fetchable URL for a stored link ("uploads/x.pdf.gz", "/uploads/y.png", "https://..."). */
export function absoluteDocumentUrl(link) {
  const s = String(link || "");
  if (!s) return "";
  if (isAbsolute(s)) return s;
  return s.startsWith("/") ? `${apiUrl}${s}` : `${apiUrl}/${s}`;
}

// link -> Promise<blobUrl>, BOUNDED.
//
// Every gzipped sheet is inflated into a blob URL and, before this, kept
// forever: opening a 128-sheet register meant 128 whole PDFs resident in
// the tab, some of them megabytes each, with nothing ever revoking them.
// That is the other half of the out-of-memory crash (the first half was
// the undestroyed PDFDocumentProxy in renderPdfThumbnail below).
//
// An LRU of BLOB_CACHE_MAX keeps the hit rate where it matters - the
// sheet being looked at, and the handful around it - while putting a
// ceiling on what a long session can accumulate. Evicted entries have
// their object URL revoked, which is what actually frees the bytes.
const BLOB_CACHE_MAX = 8;
const blobCache = new Map();

function touchBlob(key) {
  // Map preserves insertion order, so re-inserting moves it to the end
  // and the oldest entry is simply the first key.
  const v = blobCache.get(key);
  blobCache.delete(key);
  blobCache.set(key, v);
}

async function evictBlobsIfNeeded() {
  while (blobCache.size > BLOB_CACHE_MAX) {
    const oldest = blobCache.keys().next().value;
    const p = blobCache.get(oldest);
    blobCache.delete(oldest);
    try {
      const u = await p;
      if (u && u.startsWith("blob:")) URL.revokeObjectURL(u);
    } catch (e) { /* a failed entry has nothing to revoke */ }
  }
}
/**
 * Something <img>/pdf.js can open directly. Gzipped sheets (doc control
 * stores "*.pdf.gz" on R2, served raw) are inflated client-side into a blob
 * URL; everything else is just made absolute.
 */
export function resolveDocumentUrl(link) {
  const s = String(link || "");
  if (!s) return Promise.resolve("");
  if (!isGz(s)) return Promise.resolve(absoluteDocumentUrl(s));
  if (!blobCache.has(s)) {
    const p = (async () => {
      const resp = await fetch(absoluteDocumentUrl(s));
      if (!resp.ok) throw new Error(`Sheet download failed (${resp.status})`);
      const buf = new Uint8Array(await resp.arrayBuffer());
      let bytes = buf;
      // gzip magic 1f 8b - inflate; otherwise the server (or a proxy)
      // already decoded it (Content-Encoding: gzip) and this IS the pdf.
      if (buf.length > 2 && buf[0] === 0x1f && buf[1] === 0x8b) bytes = pako.inflate(buf);
      const isPdf = /\.pdf\.gz/i.test(s) || (bytes.length > 4 && bytes[0] === 0x25 && bytes[1] === 0x50);
      const blob = new Blob([bytes], { type: isPdf ? "application/pdf" : "application/octet-stream" });
      return URL.createObjectURL(blob);
    })().catch((e) => { blobCache.delete(s); throw e; });
    blobCache.set(s, p);
    evictBlobsIfNeeded();
  } else {
    touchBlob(s);
  }
  return blobCache.get(s);
}

// Small semaphore so a dropdown with 80 sheets doesn't fire 80 R2 fetches
// + pdf.js renders at once (thumbnails render 4 at a time, in order).
let active = 0;
const waiters = [];
const acquire = () => new Promise((resolve) => { if (active < 4) { active += 1; resolve(); } else waiters.push(resolve); });
const release = () => { active -= 1; const next = waiters.shift(); if (next) { active += 1; next(); } };

const thumbCache = new Map(); // link -> Promise<dataURL|null>
/** First-page thumbnail (data URL) of a PDF link; null when it can't render. */
// Cached per link AND size. The cache used to be keyed on the link
// alone, so whichever render asked first won for everyone: the sheet
// picker's 240 px thumbnail was then handed to the tracking stage as the
// texture for a 300 ft plane, which is why the drawing under the model
// was a blur no matter what the stage asked for.
// `page` (1-based, default 1): which page of a multi-page sheet.
export function renderPdfThumbnail(link, { scale = 0.2, maxWidth = 240, quality = 0.8, page: pageNo = 1 } = {}) {
  const s = String(link || "");
  if (!s) return Promise.resolve(null);
  const key = `${s}|${scale}|${maxWidth}|${quality}|p${pageNo}`;
  if (!thumbCache.has(key)) {
    const p = (async () => {
      await acquire();
      try {
        const src = await resolveDocumentUrl(s);
        if (/\.(png|jpe?g|gif|webp|bmp)(\?.*)?$/i.test(s) || /^data:image\//i.test(src)) return src;
        const pdfjsLib = await loadPdfJs();
        let pdf = null;
        try {
          pdf = await pdfjsLib.getDocument(src).promise;
          const page = await pdf.getPage(Math.min(Math.max(1, Number(pageNo) || 1), pdf.numPages || 1));
          let viewport = page.getViewport({ scale });
          if (viewport.width > maxWidth) viewport = page.getViewport({ scale: (scale * maxWidth) / viewport.width });
          const canvas = document.createElement("canvas");
          canvas.width = Math.ceil(viewport.width);
          canvas.height = Math.ceil(viewport.height);
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          const out = canvas.toDataURL("image/jpeg", quality);
          // Let the canvas go immediately rather than at the next GC: at
          // 128 sheets these add up while the loop is still running.
          canvas.width = 0;
          canvas.height = 0;
          page.cleanup();
          return out;
        } finally {
          // THE LEAK. A PDFDocumentProxy holds the parsed document AND
          // its web worker until it is destroyed, and this never
          // destroyed one. Rendering a 128-sheet register therefore left
          // 128 documents and their workers alive - "41 Creating a
          // worker from blob:" in the console of the tab that then ran
          // out of memory. The thumbnail is a dataURL; nothing needs the
          // document after this line.
          if (pdf) { try { await pdf.destroy(); } catch (e) { /* already gone */ } }
        }
      } catch (e) {
        return null;
      } finally {
        release();
      }
    })();
    thumbCache.set(key, p);
  }
  return thumbCache.get(key);
}
